/**
 * Centralized Logging Utility for LifeFinder App
 *
 * Wraps loglevel library with application-specific configuration.
 * Provides consistent logging across the application with proper log levels.
 *
 * Usage:
 *   import logger from './utils/logger';
 *
 *   logger.debug('Debug information', { data });
 *   logger.info('Informational message');
 *   logger.warn('Warning message');
 *   logger.error('Error message', error);
 *
 * Log Levels (in order of severity):
 *   - TRACE: Very detailed debugging (not used in production)
 *   - DEBUG: Detailed debugging information
 *   - INFO: General informational messages
 *   - WARN: Warning messages for potentially harmful situations
 *   - ERROR: Error messages for error events
 *   - SILENT: No logging
 */

import log from 'loglevel';

export interface ContextLogger {
  trace: (...args: unknown[]) => void;
  debug: (...args: unknown[]) => void;
  info: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
}

export interface Logger extends ContextLogger {
  setLevel: (level: log.LogLevelDesc) => void;
  getLevel: () => number;
  withContext: (context: string) => ContextLogger;
}

// Configure log level based on environment
const getDefaultLogLevel = (): string => {
  // Check environment variables first
  if (process.env.REACT_APP_LOG_LEVEL) {
    return process.env.REACT_APP_LOG_LEVEL.toLowerCase();
  }

  // Default log levels by environment
  if (process.env.NODE_ENV === 'production') {
    return 'warn'; // Only warnings and errors in production
  } else if (process.env.NODE_ENV === 'test') {
    return 'silent'; // Silence logs during tests
  } else {
    return 'debug'; // Show everything in development
  }
};

// Set the log level
log.setLevel(getDefaultLogLevel() as log.LogLevelDesc);

/**
 * Custom logger wrapper with additional features
 */
const logger: Logger = {
  /**
   * Trace level logging (very detailed)
   * Use for extremely detailed debugging
   */
  trace: (...args: unknown[]) => {
    log.trace(...args);
  },

  /**
   * Debug level logging
   * Use for general debugging information during development
   */
  debug: (...args: unknown[]) => {
    log.debug(...args);
  },

  /**
   * Info level logging
   * Use for general informational messages about app state
   */
  info: (...args: unknown[]) => {
    log.info(...args);
  },

  /**
   * Warning level logging
   * Use for potentially harmful situations that aren't errors
   */
  warn: (...args: unknown[]) => {
    log.warn(...args);
  },

  /**
   * Error level logging
   * Use for error events that might still allow the app to continue
   */
  error: (...args: unknown[]) => {
    log.error(...args);
  },

  /**
   * Set log level dynamically
   * @param {string} level - 'trace', 'debug', 'info', 'warn', 'error', 'silent'
   */
  setLevel: (level: log.LogLevelDesc) => {
    log.setLevel(level);
  },

  /**
   * Get current log level
   * @returns {number} Current log level
   */
  getLevel: (): number => {
    return log.getLevel();
  },

  /**
   * Context-aware logging helper
   * Adds context prefix to log messages
   *
   * @param {string} context - Context name (e.g., 'AuthContext', 'DeviceService')
   * @returns {object} Logger with context
   */
  withContext: (context: string): ContextLogger => {
    return {
      trace: (...args: unknown[]) => log.trace(`[${context}]`, ...args),
      debug: (...args: unknown[]) => log.debug(`[${context}]`, ...args),
      info: (...args: unknown[]) => log.info(`[${context}]`, ...args),
      warn: (...args: unknown[]) => log.warn(`[${context}]`, ...args),
      error: (...args: unknown[]) => log.error(`[${context}]`, ...args),
    };
  },
};

// Log initial configuration in development
if (process.env.NODE_ENV === 'development') {
  logger.debug('Logger initialized with level:', getDefaultLogLevel());
}

export default logger;
